import { supabase } from "./supabase";

interface ConnectionState {
  timestamp: number;
  sessionId?: string;
  accessToken?: string;
  tokenExpiry?: number;
  userId?: string;
  userEmail?: string;
  error?: string;
  queryDuration?: number;
  networkLatency?: number;
}

class ConnectionDebugger {
  private connectionHistory: ConnectionState[] = [];
  private isFirstConnection = true;

  async captureConnectionState(label: string): Promise<ConnectionState> {
    console.log(`\n🔍 Capturing connection state: ${label}`);

    const state: ConnectionState = {
      timestamp: Date.now(),
    };

    try {
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        state.error = `Auth error: ${error.message}`;
      } else if (session) {
        state.sessionId = session.access_token.substring(0, 20) + "...";
        state.accessToken = session.access_token.substring(0, 20) + "...";
        state.userId = session.user.id;
        state.userEmail = session.user.email;

        // Check token expiry
        if (session.expires_at) {
          const expiryTime = session.expires_at * 1000; // Convert to milliseconds
          state.tokenExpiry = expiryTime;
          const timeUntilExpiry = expiryTime - Date.now();
          console.log(`Token expires in: ${Math.round(timeUntilExpiry / 1000)}s`);

          if (timeUntilExpiry < 0) {
            console.warn("⚠️ Token is already expired!");
          }
        }
      } else {
        state.error = "No session found";
      }

      // Test a simple query with timing
      const queryStart = performance.now();
      const { data, error: queryError } = await Promise.race([
        supabase.from('users').select('id').limit(1),
        new Promise<any>((_, reject) =>
          setTimeout(() => reject(new Error('Query timeout after 5s')), 5000)
        )
      ]);
      const queryEnd = performance.now();

      state.queryDuration = queryEnd - queryStart;

      if (queryError) {
        state.error = `Query error: ${queryError}`;
        console.error("Query failed:", queryError);
      } else {
        console.log(`✅ Query succeeded in ${state.queryDuration.toFixed(0)}ms`);
      }

    } catch (error) {
      state.error = `Unexpected error: ${error}`;
      console.error("Capture error:", error);
    }

    this.connectionHistory.push(state);
    return state;
  }

  async compareConnections() {
    console.log("\n📊 Connection Comparison:");
    console.log("========================");

    if (this.connectionHistory.length < 2) {
      console.log("Not enough data to compare");
      return;
    }

    const first = this.connectionHistory[0];
    const latest = this.connectionHistory[this.connectionHistory.length - 1];

    console.log("\n🥇 First Connection:");
    this.logState(first);

    console.log("\n🔄 Latest Connection:");
    this.logState(latest);

    console.log("\n🔎 Differences:");

    // Compare tokens
    if (first.accessToken !== latest.accessToken) {
      console.log("❌ Access token changed!");
    } else {
      console.log("✅ Access token unchanged");
    }

    // Compare query performance
    if (first.queryDuration && latest.queryDuration) {
      const diff = latest.queryDuration - first.queryDuration;
      if (diff > 1000) {
        console.log(`❌ Query slowdown: ${diff.toFixed(0)}ms slower`);
      }
    }

    // Check for errors
    if (!first.error && latest.error) {
      console.log("❌ Error appeared in subsequent connection:", latest.error);
    }

    // Check token expiry
    if (first.tokenExpiry && latest.tokenExpiry) {
      const firstRemaining = first.tokenExpiry - first.timestamp;
      const latestRemaining = latest.tokenExpiry - latest.timestamp;

      console.log(`First connection token TTL: ${Math.round(firstRemaining / 1000)}s`);
      console.log(`Latest connection token TTL: ${Math.round(latestRemaining / 1000)}s`);
    }
  }

  private logState(state: ConnectionState) {
    console.log(`  Timestamp: ${new Date(state.timestamp).toLocaleTimeString()}`);
    console.log(`  User: ${state.userEmail || 'None'}`);
    console.log(`  Token: ${state.accessToken || 'None'}`);
    console.log(`  Query Duration: ${state.queryDuration?.toFixed(0)}ms`);
    console.log(`  Error: ${state.error || 'None'}`);
  }

  async runDiagnostics() {
    console.log("\n🏥 Running Connection Diagnostics");
    console.log("==================================");

    // Test 1: Check Supabase client state
    console.log("\n1️⃣ Checking Supabase client...");
    console.log("URL:", (supabase as any).supabaseUrl);
    console.log("Key exists:", !!(supabase as any).supabaseKey);

    // Test 2: Check auth state persistence
    console.log("\n2️⃣ Checking auth persistence...");
    const storageKeys = Object.keys(localStorage).filter(key =>
      key.includes('supabase') || key.includes('auth')
    );
    console.log("Auth storage keys:", storageKeys);

    storageKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          const parsed = JSON.parse(value);
          if (parsed.expires_at || parsed.expiresAt) {
            const expiry = new Date(parsed.expires_at || parsed.expiresAt);
            console.log(`  ${key}: expires ${expiry.toLocaleString()}`);
          } else {
            console.log(`  ${key}: ${value.substring(0, 50)}...`);
          }
        } catch {
          console.log(`  ${key}: ${value?.substring(0, 50)}...`);
        }
      }
    });

    // Test 3: Network and API health
    console.log("\n3️⃣ Testing API endpoints...");

    // Test auth endpoint
    try {
      const authStart = performance.now();
      const { data: { user } } = await supabase.auth.getUser();
      const authEnd = performance.now();
      console.log(`✅ Auth endpoint: ${(authEnd - authStart).toFixed(0)}ms`);
      console.log(`   User: ${user?.email}`);
    } catch (error) {
      console.error("❌ Auth endpoint failed:", error);
    }

    // Test database endpoint directly
    try {
      const dbStart = performance.now();
      const { data, error } = await supabase.from('users').select('count').single();
      const dbEnd = performance.now();

      if (error) {
        console.error("❌ Database endpoint failed:", error);
      } else {
        console.log(`✅ Database endpoint: ${(dbEnd - dbStart).toFixed(0)}ms`);
      }
    } catch (error) {
      console.error("❌ Database endpoint error:", error);
    }

    // Test 4: Compare headers between requests
    console.log("\n4️⃣ Checking request headers...");
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      console.log("Authorization header should be:", `Bearer ${session.access_token.substring(0, 30)}...`);

      // Check if token is JWT and decode header
      try {
        const [header] = session.access_token.split('.');
        const decoded = JSON.parse(atob(header));
        console.log("Token type:", decoded.typ);
        console.log("Algorithm:", decoded.alg);
      } catch (e) {
        console.error("Could not decode token header");
      }
    }
  }

  async monitorConnection(label: string, callback: () => Promise<any>) {
    console.log(`\n🔄 Monitoring: ${label}`);

    const startState = await this.captureConnectionState(`${label} - Before`);

    try {
      const result = await callback();
      const endState = await this.captureConnectionState(`${label} - After`);

      // Compare before/after
      if (startState.accessToken !== endState.accessToken) {
        console.warn("⚠️ Token changed during operation!");
      }

      return result;
    } catch (error) {
      console.error(`❌ Operation failed:`, error);
      throw error;
    }
  }
}

// Create singleton instance
export const connectionDebugger = new ConnectionDebugger();

// Auto-attach to window for console access
if (typeof window !== 'undefined') {
  (window as any).connectionDebugger = connectionDebugger;
  (window as any).testConnection = async () => {
    await connectionDebugger.runDiagnostics();
    await connectionDebugger.compareConnections();
  };

  console.log(`
🔧 Connection Debugger Loaded!
================================
Run these in console:
- testConnection() - Run full diagnostics
- connectionDebugger.captureConnectionState('label') - Capture current state
- connectionDebugger.compareConnections() - Compare first vs latest connection
- connectionDebugger.runDiagnostics() - Run all diagnostic tests
`);
}

export default connectionDebugger;
